/**
 * An countdown implementation that supports updating a counter
 * internally (every seconds) but only returns the count value
 * at a certain interval: e.g. if interval set to 5s, then
 * useCountdown will update its counter every 5 seconds to:
 * 60, 55, 50, etc.
 *
 * It also supports a threshold, when reached, the interval is
 * then ignored, and the counter is returned every seconds: e.g.
 * if threshold is set to 5, then the result will be:
 * 60, 55, 50, ... 15, 10, 5, 4, 3, 2, 1, 0.
 *
 * And finally, it supports a formatter to add "minutes" and
 * "seconds" around the actual values.
 *
 * @example
 *
 *  const [
 *   result,
 *   {
 *     startCountdown,
 *     stopCountdown,
 *     resetCountdown
 *   }
 *   ] =
 *    useCountdown({ timeLimit: 60 });
 *
 *  return (
 *    <div>
 *      <p>Count: {result.formattedCount}</p>
 *      <button onClick={startCountdown}>start</button>
 *      <button onClick={stopCountdown}>stop</button>
 *      <button onClick={resetCountdown}>reset</button>
 *    </div>
 *  );
 *
 */
import { useCallback, useState } from "react";
import { useBoolean, useInterval } from ".";
const messages = {
  s: {
    one: "second",
    other: "seconds"
  },
  m: {
    one: "minute",
    other: "minutes"
  }
};
const formatter = new Intl.PluralRules("en", {
  type: "cardinal"
});

const convertToMinutesSeconds = currentTimeInSeconds => {
  return {
    minutes: Math.floor(currentTimeInSeconds / 60),
    seconds: Math.round(currentTimeInSeconds % 60)
  };
};

const formatCounter = (count, format) => {
  const actualCount = convertToMinutesSeconds(count);
  let message = "";

  if (format === "m:s") {
    if (actualCount.minutes > 0) {
      message = `${actualCount.minutes} ${messages.m[formatter.select(actualCount.minutes)]} `;
    }

    message += `${actualCount.seconds} ${messages.s[formatter.select(actualCount.seconds)]}`;
  } else {
    message = `${count} ${messages.s[formatter.select(count)]}`;
  }

  return message;
};

function useCountdown(_ref) {
  let {
    /**
     * The initial time from which the timer will count down.
     */
    timeLimit = 60,

    /**
     * The interval at which the timer will display updates.
     * For example, an interval of 10 seconds means that the
     * countdown display will update every ten seconds. Therefore,
     * a timeLimit of 60 seconds will update at 50 seconds,
     * 40 seconds, 30 seconds, etc.
     */
    interval = 5,

    /**
     * The value to stop the counter.
     */
    countStop = 0,

    /**
     * The number of seconds remaining at which point the countdown
     * interval happens every second.
     */
    threshold = 5,

    /**
     * Callback that fires once the Timer has completed.
     */
    onComplete,

    /**
     * Two choices: 'm:s' or 's'.
     * - m:s yields 1 minute 6 seconds.
     * - s yields 66 seconds.
     */
    format = "s"
  } = _ref;
  const realCountStop = countStop + 1;
  const [counters, setCounters] = useState({
    count: timeLimit,
    internalCount: timeLimit
  });
  const {
    value: isCountdownRunning,
    setTrue: startCountdown,
    setFalse: stopCountdown
  } = useBoolean(false);

  const resetCountdown = () => {
    stopCountdown();
    setCounters({
      count: timeLimit,
      internalCount: timeLimit
    });
  };

  const countdownCallback = useCallback(() => {
    const newState = {
      count: counters.count,
      internalCount: counters.internalCount
    };
    const thresholdInterval = counters.internalCount <= threshold ? 1 : interval;

    if (counters.internalCount === realCountStop || timeLimit === 0) {
      typeof onComplete === "function" && onComplete();
    }

    if (counters.internalCount === countStop) {
      stopCountdown();
      return;
    }

    newState.internalCount = counters.internalCount - 1;

    if (newState.internalCount !== timeLimit && newState.internalCount % thresholdInterval === 0) {
      newState.count = counters.count - thresholdInterval;
    }

    setCounters(newState);
  }, [countStop, counters.count, counters.internalCount, interval, onComplete, realCountStop, stopCountdown, threshold, timeLimit]);
  useInterval(countdownCallback, isCountdownRunning ? 1000 : null);
  return [{
    count: counters.count,
    internalCount: counters.internalCount,
    formattedCount: formatCounter(counters.count, format)
  }, {
    startCountdown,
    stopCountdown,
    resetCountdown
  }];
}

export default useCountdown;